<template>
    <div class="assumptions">
        <div class="alert alert-warning mb-3 box-content" v-if="plan.is_published_plan === 1">
            The plan being view is currently the <em>published plan</em>. This means you are not able to make any adjustments to the assumptions or initatives. If you'd like to make any updates, <a :href="'/planning/' + client.url + '/versions'">you will need to make a new version. You can do that here.</a>
        </div>

        <div class="card mb-3">
            <div class="card-body">
                <h3>Assumptions for <em v-html="plan.name"></em></h3>
                <p>The data shown in the <em>Actual</em> fields are pulled from the transaction data. The data can be filtered using the filters below. To change the dataset, you will need to update the client information in the organization's Profile page.</p>
                <div class="row mb-3">
                    <div class="col-sm-5">
                        <!-- Name -->
                        <div class="mb-3">
                            <label class="form-label">Starting Month of Plan</label>
                            <datepicker-utc v-model="plan.start_month" 
                                :monthPicker="true"
                                inputFormat="MMM yyyy" 
                                input-class="form-control" 
                                :disabled="plan.is_published_plan === 1">
                            </datepicker-utc>
                        </div>
                    </div>
                    <div class="col-sm-5 offset-sm-1">
                        <label class="form-label mb-0">Average Gift</label>
                        <div class="row">
                            <div class="col-sm-6">
                                <!-- Name -->
                                <label class="floating_label">Override</label>
                                <div class="mb-3 input-group">
                                    <span class="input-group-text">$</span>
                                    <input type="text" 
                                        class="form-control" 
                                        v-model="plan.avg_gift"
                                        :disabled="plan.is_published_plan === 1" 
                                        @keypress="isNumber"
                                        @paste="stripNonNumbers" aria-label="Avg Gift">
                                </div>
                            </div>
                            <div class="col-sm-6">
                                <label class="floating_label">Actual</label>
                                <!-- Name -->
                                <div class="mb-3 input-group">
                                    <span class="input-group-text">$</span>
                                    <input type="text" 
                                        class="form-control" 
                                        v-model="actuals.avg_gift" 
                                        disabled aria-label="Avg Gift">
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="row mb-3">
                    <div class="col-sm-5">
                        <label class="form-label mb-0">Average New Donor Gift</label>
                        <div class="row">
                            <div class="col-sm-6">
                                <label class="floating_label">Override</label>
                                <!-- Name -->
                                <div class="mb-3 input-group">
                                    <span class="input-group-text">$</span>
                                    <input type="text" 
                                        class="form-control" 
                                        v-model="plan.new_donor_avg_gift"
                                        :disabled="plan.is_published_plan === 1" 
                                        @keypress="isNumber"
                                        @change="stripNonNumbers" aria-label="New Donor Avg Gift">
                                </div>
                            </div>
                            <div class="col-sm-6">
                                <label class="floating_label">Actual</label>
                                <!-- Name -->
                                <div class="mb-3 input-group">
                                    <span class="input-group-text">$</span>
                                    <input type="text" 
                                        class="form-control" 
                                        v-model="actuals.new_donor_avg_gift" 
                                        disabled aria-label="New Donor Avg Gift">
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="col-sm-5 offset-sm-1">
                        <label class="form-label mb-0">Average Recurring Gift</label>
                        <div class="row">
                            <div class="col-sm-6">
                                <label class="floating_label">Override</label>
                                <!-- Name -->
                                <div class="mb-3 input-group">
                                    <span class="input-group-text">$</span>
                                    <input type="text" 
                                        class="form-control" 
                                        v-model="plan.recur_avg_gift"
                                        :disabled="plan.is_published_plan === 1" 
                                        @keypress="isNumber"
                                        @change="stripNonNumbers" aria-label="Recur Avg Gift">
                                </div>
                            </div>
                            <div class="col-sm-6">
                                <label class="floating_label">Actual</label>
                                <!-- Name -->
                                <div class="mb-3 input-group">
                                    <span class="input-group-text">$</span>
                                    <input type="text" 
                                        class="form-control"
                                        v-model="actuals.recur_avg_gift" 
                                        disabled aria-label="Recur Avg Gift">
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="row mb-3">
                    <div class="col-sm-5">
                        <label class="form-label mb-0">New Subscriber Annual Attrition</label>
                        <div class="row">
                            <div class="col-sm-6">
                                <label class="floating_label">Override</label>
                                <!-- Name -->
                                <div class="mb-3 input-group">
                                    <input type="text" 
                                        class="form-control" 
                                        v-model="plan.new_subscriber_attrition"
                                        :disabled="plan.is_published_plan === 1" 
                                        @keypress="isNumber"
                                        @change="stripNonNumbers" 
                                        style="text-align: right;" aria-label="Override">
                                    <span class="input-group-text">%</span>
                                </div>
                            </div>
                            <div class="col-sm-6">
                                <!-- Name -->
                                <label class="floating_label">Actual</label>
                                <div class="mb-3 input-group">
                                    <input type="text" 
                                        class="form-control" 
                                        v-model="actuals.new_subscriber_attrition" 
                                        disabled 
                                        style="text-align: right;" aria-label="Actual">
                                    <span class="input-group-text">%</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="col-sm-5 offset-sm-1">
                        <label class="form-label mb-0">Existing Subscriber Annual Attrition</label>
                        <div class="row">
                            <div class="col-sm-6">
                                <label class="floating_label">Override</label>
                                <!-- Name -->
                                <div class="mb-3 input-group">
                                    <input type="text" 
                                        class="form-control" 
                                        v-model="plan.existing_subscriber_attrition"
                                        :disabled="plan.is_published_plan === 1" 
                                        @keypress="isNumber"
                                        @change="stripNonNumbers" 
                                        style="text-align: right;" aria-label="Override">
                                    <span class="input-group-text">%</span>
                                </div>
                            </div>
                            <div class="col-sm-6">
                                <label class="floating_label">Actual</label>
                                <!-- Name -->
                                <div class="mb-3 input-group">
                                    <input type="text" 
                                        class="form-control" 
                                        v-model="actuals.existing_subscriber_attrition" 
                                        disabled 
                                        style="text-align: right;" aria-label="Actual">
                                    <span class="input-group-text">%</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="row mb-3">
                    <div class="col-sm-5">
                        <label class="form-label mb-0">Active Email File Size</label>
                        <div class="row">
                            <div class="col-sm-6">
                                <label class="floating_label">Override</label>
                                <!-- Name -->
                                <div class="mb-3">
                                    <input type="text" 
                                        class="form-control" 
                                        v-model="plan.starting_email_size"
                                        :disabled="plan.is_published_plan === 1" 
                                        @keypress="isNumber"
                                        @change="stripNonNumbers" aria-label="Override">
                                </div>
                            </div>
                            <div class="col-sm-6">
                                <label class="floating_label">Actual</label>
                                <!-- Name -->
                                <div class="mb-3">
                                    <input type="text" 
                                        class="form-control" 
                                        v-model="actuals.starting_email_size" 
                                        disabled aria-label="Actual">
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="col-sm-5 offset-sm-1">
                        <label class="form-label mb-0">Previous Year's Donors with Emails</label>
                        <div class="row">
                            <div class="col-sm-6">
                                <label class="floating_label">Override</label>
                                <!-- Name -->
                                <div class="mb-3">
                                    <input type="text" 
                                        class="form-control" 
                                        v-model="plan.existing_donors"
                                        :disabled="plan.is_published_plan === 1" 
                                        @keypress="isNumber"
                                        @change="stripNonNumbers" aria-label="Override">
                                </div>
                            </div>
                            <div class="col-sm-6">
                                <label class="floating_label">Actual</label>
                                <!-- Name -->
                                <div class="mb-3">
                                    <input type="text" 
                                        class="form-control" 
                                        v-model="actuals.existing_donors" 
                                        disabled aria-label="Actual">
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="row mb-3">
                    <div class="col-sm-5">
                        <label class="form-label mb-0">Lifetime Value</label>
                        <div class="row">
                            <div class="col-sm-6">
                                <label class="floating_label">Override</label>
                                <!-- Name -->
                                <div class="mb-3 input-group">
                                    <span class="input-group-text">$</span>
                                    <input type="text" 
                                        class="form-control" 
                                        v-model="plan.lifetime_value"
                                        :disabled="plan.is_published_plan === 1" 
                                        @keypress="isNumber"
                                        @change="stripNonNumbers" aria-label="Lifetime Value">
                                </div>
                            </div>
                            <div class="col-sm-6">
                                <label class="floating_label">Actual</label>
                                <!-- Name -->
                                <div class="mb-3 input-group">
                                    <span class="input-group-text">$</span>
                                    <input type="text" 
                                        class="form-control" 
                                        v-model="actuals.lifetime_value" 
                                        disabled aria-label="Lifetime Value">
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="col-sm-5 offset-sm-1">
                        <label class="form-label mb-0">Donor Retention</label>
                        <div class="row">
                            <div class="col-sm-6">
                                <label class="floating_label">Override</label>
                                <!-- Name -->
                                <div class="mb-3">
                                    <input type="text" 
                                        class="form-control" 
                                        v-model="plan.donor_retention"
                                        :disabled="plan.is_published_plan === 1" 
                                        @keypress="isNumber"
                                        @change="stripNonNumbers" aria-label="Override">
                                </div>
                            </div>
                            <div class="col-sm-6">
                                <!-- Name -->
                                <label class="floating_label">Actual</label>
                                <div class="mb-3">
                                    <input type="text" 
                                        class="form-control" 
                                        v-model="actuals.donor_retention" 
                                        disabled aria-label="Actual">
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="row mb-3">
                    <div class="col-sm-5">
                        <div class="row">
                            <div class="col-6">
                                <label class="form-label">Avg. Rev. per Donor:</label>
                                <em>{{ currency(actuals.rev_per_donor) }}</em>
                            </div>
                            <div class="col-6">
                                <label class="form-label">Attrition Rate:</label>
                                <em>{{ percentage(actuals.attrition_rate) }}</em>
                            </div>
                        </div>
                    </div>
                    <div class="col-sm-5 offset-sm-1">
                        <label class="form-label mb-0">Annual Recurring Donor Attrition</label>
                        <div class="row">
                            <div class="col-sm-6">
                                <label class="floating_label">Override</label>
                                <div class="mb-3 input-group">
                                    <input type="text" 
                                        class="form-control"
                                        v-model="plan.recurring_attrition" 
                                        @keypress="isNumber"
                                        :disabled="plan.is_published_plan === 1"
                                        @change="stripNonNumbers" 
                                        style="text-align: right;" aria-label="Override">
                                    <span class="input-group-text">%</span>
                                </div>
                            </div>
                            <div class="col-sm-6">
                                <!-- Name -->
                                <label class="floating_label">Actual</label>
                                <div class="mb-3 input-group">
                                    <input type="text" 
                                        class="form-control"
                                        v-model="actuals.recurring_attrition" 
                                        disabled
                                        style="text-align: right;" aria-label="Actual">
                                    <span class="input-group-text">%</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="card mb-3">
            <div class="card-body">
                <h3>Data Inclusion Filter</h3>
                <query-inclusion-builder 
                    :client="client" 
                    v-model="plan.data_filter"
                    :in_card="true"  
                    :add_blank="false" 
                    ref="filter">
                </query-inclusion-builder>
            </div>
        </div>
        <div class="row">
            <div class="col">
                <!-- Success Message -->
                <div class="alert alert-success mb-3" v-if="form.successful">
                    Your information has been updated!
                </div>
                <div class="alert alert-danger mb-3" v-if="form.error">
                    There was an error saving your information. Please make the corrections noted above.
                </div>
                <!-- Update Button -->
                <div class="mb-3">
                    <button type="submit" 
                        class="btn btn-primary"
                        @click.prevent="update"
                        :disabled="form.busy || plan.is_published_plan === 1">
                        <img class="icon" src="/img/icons/avid-icons/save.svg" alt=""> Save
                    </button>
                    <button type="button" 
                        class="btn btn-secondary" 
                        style="ms-2"
                        @click.prevent="getManualActuals"
                        :disabled="form.busy || plan.is_published_plan === 1">
                        <i class="fas fa-sync"></i> Refresh Stats
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import { defineComponent } from 'vue';
    export default defineComponent({
        name: 'Assumptions',
        props: {
            plan: {
                type: Object,
                required: true
            },
            client: {
                type: Object,
                required: true
            }
        },
        data() {
            return {
                form: {
                    successful: false,
                    busy: false,
                    error: false
                },
                actuals: {
                    avg_gift: "Loading...",
                    new_donor_avg_gift: "Loading...",
                    recur_avg_gift: "Loading...",
                    lifetime_value: "Loading...",
                    starting_email_size: "Loading...",
                    new_subscriber_attrition: "Loading...",
                    existing_subscriber_attrition: "Loading...",
                    donor_retention: "Loading...",
                    existing_donors: "Loading...",
                    attrition_rate: "Loading...",
                    rev_per_donor: "Loading...",
                    recurring_attrition: "Loading..."
                }
            };
        },
        beforeMount() {
            //Change the format of the percentages (I'll change it back before saving)
            this.plan.avg_gift = this.formatMoney(this.plan.avg_gift);
            this.plan.new_donor_avg_gift = this.formatMoney(this.plan.new_donor_avg_gift);
            this.plan.recur_avg_gift = this.formatMoney(this.plan.recur_avg_gift);
            this.plan.lifetime_value = this.formatMoney(this.plan.lifetime_value);
            this.plan.starting_email_size = this.formatNumber(this.plan.starting_email_size);
            this.plan.existing_donors = this.formatNumber(this.plan.existing_donors);
            this.plan.new_subscriber_attrition = this.formatNumber(this.plan.new_subscriber_attrition * 100);
            this.plan.existing_subscriber_attrition = this.formatNumber(this.plan.existing_subscriber_attrition * 100);
            this.plan.donor_retention = this.formatNumber(this.plan.donor_retention * 100);
            this.plan.recurring_attrition = this.formatNumber(this.plan.recurring_attrition * 100);
        },
        mounted() {
            this.getActuals();
        },
        methods: {
            stripNonNumbers(e) {
                // Prevent the default pasting event and stop bubbling
                e.preventDefault();
                e.stopPropagation();
                // Get the clipboard data
                const paste = (e.clipboardData || window.clipboardData).getData('text/plain');
                const cleanPaste = paste.replace(/[^\d.-]/g, '');

                if (document.queryCommandSupported("insertText")) {
                    document.execCommand("insertText", false, cleanPaste);
                } else {
                    document.execCommand("paste", false, cleanPaste);
                }
            },
            formatMoney(value) {
                if (value === null || value === undefined || value === '') {
                    return "0.00";
                }
                const parts = value.toString().split(".");
                parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
                if (parts[1] !== undefined && parts[1].length > 2) {
                    parts[1] = parts[1].substr(0, 2);
                    if (parts[1].length === 1) parts[1] = parts[1] + "0";
                } else if (parts[1] === undefined) {
                    parts[0] = parts[0] + ".00";
                }
                return parts.join(".");
            },
            formatNumber(value) {
                if (value === undefined || value === '') {
                    return 0;
                }
                const parts = value.toString().split(".");
                parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
                if (parts.length > 1) {
                    parts[1] = (parts[1].length > 2 ? parts[1].substring(0, 2) : parts[1]);
                }
                return parts.join(".");
            },
            isNumber(evt) {
                evt = evt || window.event;
                const charCode = evt.which ? evt.which : evt.keyCode;
                if ((charCode > 31 && (charCode < 48 || charCode > 57)) && charCode !== 46 && charCode !== 44) {
                    evt.preventDefault();
                } else {
                    return true;
                }
            },
            getManualActuals() {
                if (!this.$refs.filter.isFormValid()) {
                    this.form.error = true;
                    this.form.busy = false;
                    return;
                }

                this.form.busy = true;
                Object.keys(this.actuals).forEach(key => {
                    this.actuals[key] = "Loading...";
                });
                this.getActuals();
            },
            getActuals() {
                if (!this.$refs.filter.isFormValid()) {
                    this.form.error = true;
                    this.form.busy = false;
                    return;
                }

                const data = {
                    client: this.client,
                    data_filter: this.plan.data_filter
                };

                window.axios.post('/api/bigquery/get_actuals', data)
                    .then(response => {
                        if (response.status === 200) {
                            const data = response.data.data;
                            const formatters = {
                                avg_gift: this.formatMoney,
                                new_donor_avg_gift: this.formatMoney,
                                recur_avg_gift: this.formatMoney,
                                lifetime_value: this.formatMoney,
                                starting_email_size: this.formatNumber,
                                existing_donors: this.formatNumber,
                                new_subscriber_attrition: (v) => this.formatNumber(parseFloat(v * 100)),
                                existing_subscriber_attrition: (v) => this.formatNumber(parseFloat(v * 100)),
                                donor_retention: (v) => this.formatNumber(parseFloat(v * 100)),
                                rev_per_donor: (v) => this.formatNumber(parseFloat(v)),
                                attrition_rate: (v) => this.formatNumber(parseFloat(v)),
                                recurring_attrition: (v) => this.formatNumber(parseFloat(v * 100))
                            };

                            Object.keys(formatters).forEach(key => {
                                this.actuals[key] = !isNaN(data[key]) ? formatters[key](data[key]) : data[key];
                            });
                        } else {
                            Object.keys(this.actuals).forEach(key => {
                                this.actuals[key] = "Failed";
                            });
                        }
                        this.form.busy = false;
                    });
            },
            update() {
                if (!this.$refs.filter.isFormValid()) {
                    this.form.error = true;
                    this.form.busy = false;
                    return;
                }

                const fields = {
                    avg_gift: parseFloat,
                    new_donor_avg_gift: parseFloat,
                    recur_avg_gift: parseFloat,
                    lifetime_value: parseFloat,
                    starting_email_size: parseInt,
                    existing_donors: parseInt,
                    new_subscriber_attrition: (v) => parseFloat(v) / 100,
                    existing_subscriber_attrition: (v) => parseFloat(v) / 100,
                    donor_retention: (v) => parseFloat(v) / 100,
                    recurring_attrition: (v) => parseFloat(v) / 100
                };

                Object.entries(fields).forEach(([key, parser]) => {
                    this.plan[key] = this.plan[key] === "" ? 0 : parser(("" + this.plan[key]).replace(/,/g, ''));
                });

                this.form.busy = true;
                window.axios.put('/api/annual_plan/' + this.plan.id, this.plan)
                    .then(response => {
                        this.form.busy = false;
                        if (response.status === 200) {
                            this.form.successful = true;
                            setTimeout(() => { this.form.successful = false; }, 5000);
                        } else {
                            this.form.error = true;
                            setTimeout(() => { this.form.error = false; }, 10000);
                        }
                    });

                // Format values back for display
                this.plan.avg_gift = this.formatMoney(this.plan.avg_gift);
                this.plan.new_donor_avg_gift = this.formatMoney(this.plan.new_donor_avg_gift);
                this.plan.recur_avg_gift = this.formatMoney(this.plan.recur_avg_gift);
                this.plan.lifetime_value = this.formatMoney(this.plan.lifetime_value);
                this.plan.starting_email_size = this.formatNumber(this.plan.starting_email_size);
                this.plan.existing_donors = this.formatNumber(this.plan.existing_donors);
                this.plan.new_subscriber_attrition = this.formatNumber(this.plan.new_subscriber_attrition * 100);
                this.plan.existing_subscriber_attrition = this.formatNumber(this.plan.existing_subscriber_attrition * 100);
                this.plan.donor_retention = this.formatNumber(this.plan.donor_retention * 100);
                this.plan.recurring_attrition = this.formatNumber(this.plan.recurring_attrition * 100);
            }
        }
    });
</script>
